import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeftRight, Download, Search } from 'lucide-react'
import { getStockMovements } from '../../api/inventory'
import Spinner from '../../components/Spinner'
import { FilterChips } from '../../components/PageParts'
import { fmtDateTime, localToday } from '../../utils/format'
import { periodRange } from '../sales/salesShared'
import InventoryTabs from './InventoryTabs'
import { MOVE_TYPES, qty, useLocations } from './inventoryShared'

const PERIODS = [
  { id: 'all', label: 'All time' },
  { id: 'week', label: 'Last 7 days' },
  { id: 'month', label: 'This month' },
  { id: 'last-month', label: 'Last month' },
]

// Stock moved between locations and bins, plus manual adds and removals.
export default function StockMovements() {
  const locations = useLocations()
  const [period, setPeriod] = useState('all')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [type, setType] = useState('all')
  const [search, setSearch] = useState('')

  useEffect(() => {
    setLoading(true)
    const [start, end] = period === 'all' ? [] : periodRange(period)
    getStockMovements({ start, end, limit: 1000 })
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [period])

  const types = useMemo(() => {
    const present = [...new Set(rows.map((r) => r.move_type))]
    return [{ id: 'all', label: 'All', count: rows.length }, ...present.map((t) => ({ id: t, label: MOVE_TYPES[t] || t, count: rows.filter((r) => r.move_type === t).length }))]
  }, [rows])

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return rows.filter((r) => (type === 'all' || r.move_type === type)
      && (!term || [r.name, r.sku, r.notes, r.from_location, r.to_location].join(' ').toLowerCase().includes(term)))
  }, [rows, type, search])

  const exportCsv = () => {
    const head = ['When', 'Item', 'SKU', 'What', 'From', 'To', 'Quantity', 'Cost each', 'Note']
    const lines = shown.map((r) => [r.movement_date, r.name, r.sku, MOVE_TYPES[r.move_type] || r.move_type,
      [r.from_location, r.from_area].filter(Boolean).join(' / '), [r.to_location, r.to_area].filter(Boolean).join(' / '), r.quantity, r.unit_cost, r.notes])
    const csv = [head, ...lines].map((a) => a.map((x) => `"${String(x ?? '').replaceAll('"', '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `stock_moves_${localToday()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ci-page">
      <InventoryTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Moves</h1>
            <div className="ci-title-meta">Stock moved between locations and bins. Sales, receiving and adjustments show on each item and in the books.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" onClick={exportCsv} disabled={!shown.length}><Download size={15} />Export</button>
          {locations?.length > 1 && <Link className="ci-btn ci-btn-primary" to="/inventory/move"><ArrowLeftRight size={15} />Move stock</Link>}
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search moves">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Item, location or note…" />
        </label>
        <select className="ci-select" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period">
          {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>

      {types.length > 2 && <FilterChips options={types} value={type} onChange={setType} />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>When</th>
                <th>Item</th>
                <th>What</th>
                <th className="hide-sm">From</th>
                <th className="hide-sm">To</th>
                <th className="num">Qty</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6"><Spinner label="Loading moves…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">{rows.length ? 'No moves match.' : 'No stock has been moved in this period.'}</div></td></tr>
              ) : shown.map((r, i) => (
                <tr key={r.movement_id ?? i}>
                  <td className="ci-muted-cell">{fmtDateTime(r.movement_date)}</td>
                  <td>
                    {r.product_id ? <Link className="ci-je-link" to={`/inventory/stock/${r.product_id}`}>{r.name || r.sku}</Link> : (r.name || r.sku)}
                    <span className="cell-sub">{r.sku}</span>
                  </td>
                  <td>{MOVE_TYPES[r.move_type] || r.move_type}{r.notes && <span className="cell-sub">{r.notes}</span>}</td>
                  <td className="hide-sm">{[r.from_location, r.from_area].filter(Boolean).join(' · ') || '–'}</td>
                  <td className="hide-sm">{[r.to_location, r.to_area].filter(Boolean).join(' · ') || '–'}</td>
                  <td className="num ci-strong">{qty(r.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} {shown.length === 1 ? 'move' : 'moves'}</span>
        </div>
      </div>
    </div>
  )
}
